import React, {useEffect, useState} from "react";
import {Link, useLocation, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import axios from "axios";
import Moment from "react-moment";
import {useTranslation} from "react-i18next";
import "moment/locale/ru";

import {buildBackNavigationTarget} from "helpers/backNavigation";
import {useCreateReport} from "hooks/useCreateReport";
import {usePageRestore} from "hooks/usePageRestore";
import {declinations} from "helpers/declinations";
import {getSortedTestsByDate} from "helpers/tests";
import {BackButton} from "components/BackButton";
import {Layout} from "components/Layout";
import {Range} from "components/Range";

/**
 * @api {get} /statistic/pupils/:id статистика ученика
 * @apiGroup statistic
 * @apiName статистика ученика
 *
 * @apiParam {String} id ID ученика
 *
 * @apiDescription <p>Страница статистики выбранного ученика</p>
 * <p>Компонент <code>StatisticPupil.js</code> <a href="../client/src/pages/statistic/StatisticPupil.js">ссылка на файл</a></p>
 */

export const StatisticPupil = () => {
    const { t, i18n } = useTranslation("statistic");
    const pupilId = useParams().id;
    const location = useLocation();
    const [norm, setNorm] = useState(0);
    const [tests, setTests] = useState([]);
    const [nullNorm, setNullNorm] = useState(0);
    const { filters } = useSelector((state) => state.statistic);
    const [passedTests, setPassedTests] = useState([]);
    const [notPassedTests, setNotPassedTests] = useState([]);
    const [expandedTests, setExpandedTests] = useState({});
    const [expandedNotPassedTests, setExpandedNotPassedTests] = useState({});
    const { createReport, isReportLoading } = useCreateReport();
    const pageRestoreKey = `statistic-pupil:${pupilId}`;

    usePageRestore({
        pageKey: pageRestoreKey,
        getState: () => ({
            expandedTests,
            expandedNotPassedTests,
        }),
        restoreState: (state) => {
            setExpandedTests(state?.expandedTests || {});
            setExpandedNotPassedTests(state?.expandedNotPassedTests || {});
        },
        watch: {
            expandedTests,
            expandedNotPassedTests,
        },
        isReady: !!tests.length,
    });

    useEffect(() => {
        if (pupilId) {
            const arr = [];

            if (filters.date) {
                arr.push(`date=${filters.date}`);
            }

            if (filters.condition) {
                arr.push(`condition=${filters.condition}`);
            }

            (async () => {
                const { data } = await axios.get(`statistic/pupils/${pupilId}?${arr.join("&")}`);

                setTests(data);
            })();
        }
    }, [pupilId, filters]);

    useEffect(() => {
        if (tests.length) {
            const passed = tests.find(({ _id }) => !!_id);
            const notPassed = tests.find(({ _id }) => _id === null);
            const empty = notPassed?.total;
            const passedFiltered = [];

            if (passed) {
                for (const test of passed.tests) {
                    const isExistIndex = passedFiltered.findIndex(
                        ({ conditionId }) => test.conditionId === conditionId,
                    );

                    if (isExistIndex === -1) {
                        passedFiltered.push({ ...test, allTests: [test] });
                    } else {
                        passedFiltered[isExistIndex].allTests.push(test);
                        if (test.date > passedFiltered[isExistIndex].date) {
                            passedFiltered[isExistIndex].date = test.date;
                        }
                    }
                }
            }

            if (notPassed) {
                const notPassedFiltered = [];
                for (const test of notPassed.tests) {
                    const isExistIndex = notPassedFiltered.findIndex(
                        ({ conditionId }) => test.conditionId === conditionId,
                    );
                    if (isExistIndex === -1) {
                        notPassedFiltered.push({ ...test, allTests: [test] });
                    } else {
                        notPassedFiltered[isExistIndex].allTests.push(test);
                        if (test.date > notPassedFiltered[isExistIndex].date) {
                            notPassedFiltered[isExistIndex].date = test.date;
                        }
                    }
                }
                setNotPassedTests(getSortedTestsByDate(notPassedFiltered));
            }

            setPassedTests(getSortedTestsByDate(passedFiltered));
            const totalPassed = passedFiltered.reduce((sum, g) => sum + g.allTests.length, 0);
            setNorm(Number(totalPassed));
            setNullNorm(Number(empty || !!empty));
        }
    }, [tests]);

    const createReportHandler = () => {
        const testsQuery = passedTests.flatMap(({ allTests }) => allTests.map(({ _id }) => _id));

        createReport(
            `statistic/pupil-report/${pupilId}?tests=${testsQuery}`,
            `${t("report", { ns: "translation" })}_${tests[0]?.pupil.surname}_${tests[0]?.pupil.name}.xlsx`,
        );
    };

    return (
        <Layout>
            <div className="page statistic-details-page">
                <header className="page__header">
                    <BackButton />
                    <p className="page__title statistic-details-page__title">{t("statistic")}</p>
                    <Link
                        to={`/statistic/pupils/${pupilId}/filters`}
                        className={
                            !!Object.values(filters).filter((val) => !!val.length).length
                                ? `icon-btn page__icon-btn page__icon-btn_right icon-btn_filter icon-btn_filter_pupil icon-btn_filter_active`
                                : `icon-btn page__icon-btn icon-btn_filter_pupil page__icon-btn_right icon-btn_filter`
                        }
                    >
                        {!!Object.values(filters).filter((val) => !!val.length).length && (
                            <span>{Object.values(filters).filter((val) => !!val.length).length}</span>
                        )}
                    </Link>
                </header>

                <div className="statistic-details-page__main">
                    <div className="statistic-condition" style={{ cursor: "default" }}>
                        <p className="statistic-condition__title">
                            {tests[0]?.pupil.surname} {tests[0]?.pupil.name}
                        </p>
                        <Range total={norm + nullNorm} norm={norm} notNorm={nullNorm} />
                        <div className="statistic-condition__footer">
                            <span className="statistic-condition__indicator green">{norm}</span>
                            <span className="statistic-condition__indicator blue">{nullNorm}</span>
                            <p className="statistic-condition__members">
                                {norm + nullNorm} {declinations(norm + nullNorm, "test", i18n.language)}
                            </p>
                        </div>
                    </div>

                    <div className="solution-page__contacts">
                        <Link
                            to={`/pupils/${pupilId}?from=statistic-pupils&to=/statistic/pupils/${pupilId}`}
                            className="profile-page__btn statistic-pupils__profile-btn"
                        >
                            {t("go_to_profile")}
                        </Link>
                    </div>
                </div>

                <div className="statistic-pupils">
                    <div className="statistic-pupils__wrapper">
                        <div className="statistic-pupils__group">
                            {!!passedTests.length && (
                                <div
                                    className="statistic-reports__btn statistic-reports__btn-class"
                                    onClick={createReportHandler}
                                >
                                    {isReportLoading ? (
                                        <p>{t("report_loading", { ns: "translation" })}</p>
                                    ) : (
                                        <p>{t("create_report", { ns: "translation" })}</p>
                                    )}
                                </div>
                            )}

                            <div className="statistic-pupils__header">
                                <p className="statistic-pupils__title green">{t("passed_test")}</p>
                                <span className="statistic-pupils__count">{norm}</span>
                            </div>
                            <div>
                                {passedTests.map((group) => {
                                    if (group.allTests.length === 1) {
                                        return (
                                            <Link
                                                to={buildBackNavigationTarget(
                                                    `/solutions/${group._id}`,
                                                    location,
                                                    pageRestoreKey,
                                                )}
                                                className="statistic-pupils__item"
                                                key={group._id}
                                            >
                                                <div>
                                                    <p className="statistic-pupils__name">{group.condition}</p>
                                                    <p className="statistic-pupils__details">
                                                        <Moment locale={i18n.language} format="HH:mm, DD MMM, YYYY">
                                                            {group.date}
                                                        </Moment>
                                                    </p>
                                                    {(group.createdBy || group.psych) && (
                                                        <p className="statistic-pupils__assigned">
                                                            {t("assigned_by")}: {(group.createdBy || group.psych).surname}{" "}
                                                            {(group.createdBy || group.psych).name}
                                                        </p>
                                                    )}
                                                </div>
                                            </Link>
                                        );
                                    }

                                    const isExpanded = !!expandedTests[group.conditionId];
                                    return (
                                        <React.Fragment key={group.conditionId}>
                                            <div
                                                className="statistic-pupils__item statistic-pupils__item_group"
                                                onClick={() =>
                                                    setExpandedTests((prev) => ({
                                                        ...prev,
                                                        [group.conditionId]: !prev[group.conditionId],
                                                    }))
                                                }
                                            >
                                                <div className="statistic-pupils__item-content">
                                                    <p className="statistic-pupils__name">{group.condition}</p>
                                                </div>
                                                <p className="statistic-pupils__details_count">
                                                    {group.allTests.length}{" "}
                                                    {declinations(group.allTests.length, "test", i18n.language)}
                                                </p>
                                            </div>
                                            {isExpanded &&
                                                group.allTests
                                                    .slice()
                                                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                                                    .map((item) => (
                                                        <Link
                                                            key={item._id}
                                                            to={buildBackNavigationTarget(
                                                                `/solutions/${item._id}`,
                                                                location,
                                                                pageRestoreKey,
                                                            )}
                                                            className="statistic-pupils__item statistic-pupils__item_sub"
                                                        >
                                                            <div>
                                                                <p className="statistic-pupils__details">
                                                                    <Moment locale={i18n.language} format="HH:mm, DD MMM, YYYY">
                                                                        {item.date}
                                                                    </Moment>
                                                                </p>
                                                                {(item.createdBy || item.psych) && (
                                                                    <p className="statistic-pupils__assigned">
                                                                        {t("assigned_by")}: {(item.createdBy || item.psych).surname}{" "}
                                                                        {(item.createdBy || item.psych).name}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </Link>
                                                    ))}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="statistic-pupils__group">
                            <div className="statistic-pupils__header">
                                <p className="statistic-pupils__title blue">{t("not_passed_test")}</p>
                                <span className="statistic-pupils__count">{nullNorm}</span>
                            </div>
                            <div>
                                {notPassedTests.map((group) => {
                                    if (group.allTests.length === 1) {
                                        return (
                                            <div key={group._id} className="statistic-pupils__item">
                                                <div>
                                                    <p className="statistic-pupils__name">{group.condition}</p>
                                                    <p className="statistic-pupils__details">
                                                        <Moment locale={i18n.language} format="HH:mm, DD MMM, YYYY">
                                                            {group.date}
                                                        </Moment>
                                                    </p>
                                                    {(group.createdBy || group.psych) && (
                                                        <p className="statistic-pupils__assigned">
                                                            {t("assigned_by")}: {(group.createdBy || group.psych).surname}{" "}
                                                            {(group.createdBy || group.psych).name}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }

                                    const isExpanded = !!expandedNotPassedTests[group.conditionId];
                                    return (
                                        <React.Fragment key={group.conditionId}>
                                            <div
                                                className="statistic-pupils__item statistic-pupils__item_group"
                                                onClick={() =>
                                                    setExpandedNotPassedTests((prev) => ({
                                                        ...prev,
                                                        [group.conditionId]: !prev[group.conditionId],
                                                    }))
                                                }
                                            >
                                                <div className="statistic-pupils__item-content">
                                                    <p className="statistic-pupils__name">{group.condition}</p>
                                                </div>
                                                <p className="statistic-pupils__details_count">
                                                    {group.allTests.length}{" "}
                                                    {declinations(group.allTests.length, "test", i18n.language)}
                                                </p>
                                            </div>
                                            {isExpanded &&
                                                group.allTests
                                                    .slice()
                                                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                                                    .map((item) => (
                                                        <div
                                                            key={item._id}
                                                            className="statistic-pupils__item statistic-pupils__item_sub"
                                                        >
                                                            <div>
                                                                <p className="statistic-pupils__details">
                                                                    <Moment locale={i18n.language} format="HH:mm, DD MMM, YYYY">
                                                                        {item.date}
                                                                    </Moment>
                                                                </p>
                                                                {(item.createdBy || item.psych) && (
                                                                    <p className="statistic-pupils__assigned">
                                                                        {t("assigned_by")}: {(item.createdBy || item.psych).surname}{" "}
                                                                        {(item.createdBy || item.psych).name}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
